export const OHLGS_SETUP_CATALOG_STORAGE_KEY = "setup:ohlgs-setup:catalog";
export const OHLGS_ASSESSMENT_STORAGE_KEY = "ohlgs-assessment-responses";

export function readStoredJson(key, fallbackValue) {
  if (typeof window === "undefined") {
    return fallbackValue;
  }

  try {
    const rawValue = window.localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : fallbackValue;
  } catch (error) {
    console.warn(`Unable to read local storage key "${key}":`, error);
    return fallbackValue;
  }
}

export function writeStoredJson(key, value) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Unable to write local storage key "${key}":`, error);
  }
}

function splitLines(value) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function normalizeStatus(value) {
  return String(value || "").trim().toUpperCase() === "INACTIVE" ? "INACTIVE" : "ACTIVE";
}

function isActiveStatus(value) {
  return normalizeStatus(value) === "ACTIVE";
}

export function getLocationCode(locationNode) {
  if (!locationNode) return "";

  if (locationNode.level === "region" || locationNode.level === "district") {
    return locationNode.orgUnitId || locationNode.id || locationNode.slug || "";
  }

  return "";
}

export function getStoredOhlgsCatalog() {
  const catalog = readStoredJson(OHLGS_SETUP_CATALOG_STORAGE_KEY, []);
  return Array.isArray(catalog) ? catalog : [];
}

export function buildSectionsFromCatalog(catalog) {
  return (Array.isArray(catalog) ? catalog : [])
    .filter((section) => isActiveStatus(section?.status))
    .map((section, sectionIndex) => ({
      id: section.id || `kpa-${sectionIndex + 1}`,
      code: section.code || section.kpaCode || `KPA ${sectionIndex + 1}`,
      title: section.kpaName || section.title || "Untitled KPA",
      notes: splitLines(section.kpaDescription || section.notes),
      indicators: (Array.isArray(section.kpis) ? section.kpis : [])
        .filter((kpi) => isActiveStatus(kpi?.status))
        .map((kpi, kpiIndex) => ({
          id: kpi.id || `${section.id || `kpa-${sectionIndex + 1}`}::${kpi.code || kpiIndex + 1}`,
          code: kpi.code || `${sectionIndex + 1}.${kpiIndex + 1}`,
          statement: splitLines(kpi.statement),
          scoringGuide: splitLines(kpi.scoringGuide),
          scored: kpi.scored !== "false",
          status: kpi.status || "Active",
        })),
    }))
    .filter((section) => section.indicators.length > 0);
}

export function createInitialResponsesFromSections(sections) {
  return (Array.isArray(sections) ? sections : []).reduce((acc, section) => {
    (Array.isArray(section.indicators) ? section.indicators : []).forEach((indicator) => {
      acc[indicator.id] = {
        score: "",
        comment: "",
      };
    });

    return acc;
  }, {});
}

export function getAssessmentRecord(rows, { level, locationCode, year, quarter }) {
  return (Array.isArray(rows) ? rows : []).find(
    (item) =>
      String(item?.level || "") === String(level || "") &&
      String(item?.locationCode || "") === String(locationCode || "") &&
      String(item?.year || "") === String(year || "") &&
      String(item?.quarter || "") === String(quarter || ""),
  );
}

export function upsertAssessmentRecord(rows, nextRecord) {
  const items = Array.isArray(rows) ? rows : [];
  return [
    nextRecord,
    ...items.filter(
      (item) =>
        !(
          String(item?.level || "") === String(nextRecord?.level || "") &&
          String(item?.locationCode || "") === String(nextRecord?.locationCode || "") &&
          String(item?.year || "") === String(nextRecord?.year || "") &&
          String(item?.quarter || "") === String(nextRecord?.quarter || "")
        ),
    ),
  ];
}
